/**
 * Sizing shared by the thematic-meetings pages, named here so the pages and the neighbouring
 * report pages cannot drift apart. Values are written out where the design lands between two steps
 * of the scale, which `app.css` declares in real pixels.
 */

/** Block title. Declared beside the plan's shared card, which needs it too and cannot reach into a
 *  page; re-exported here so the pages of this feature keep reading their sizing from one module. */
export { MEETINGS_TITLE } from '@/components/MeetingPlan/meetingPlanStyles';

/** Section title inside the list ("Upcoming meetings"): 20px, regular weight. */
export const SECTION_TITLE = 'text-[20px]';

/** Field label in the plan header (Objective, Context): 20px medium. */
export const FIELD_LABEL = 'text-[20px]';

/** Major breathing room inside the list's glass container: 64px (`gap-16` would only reach 56px). */
export const GLASS_STACK_GAP = 'gap-[64px]';

/** Gap between two stacked cards: 24px (`gap-6` would only reach 21px). The list's meeting cards,
 *  its quick-stats rows and the plan's sections all keep this one rhythm. */
export const CARD_STACK_GAP = 'gap-[24px]';

/** Gap between recent-activity entries: 32px (`gap-8` would only reach 28px). */
export const ACTIVITY_STACK_GAP = 'gap-[32px]';

/** Where an entry's 10px marker sits so it reads as belonging to the first line rather than to
 *  the whole entry: half of what the 20px line box leaves around it, (20 - 10) / 2 = 5px. Named
 *  here so the offset stays derived from the two sizes it depends on instead of being retuned by
 *  eye whenever an entry gains or loses a line. */
export const MARKER_TOP_OFFSET = 'mt-1.25';

/** Gap between the numbered questions of a plan section: 32px, room for a wrapped question to breathe. */
export const QUESTION_STACK_GAP = 'gap-[32px]';

/** The page's two columns: main content and a 343px side column. The banner and the glass
 *  container are stacked inside the first column, so the banner's button always ends at the same
 *  edge as the meeting list under it without a second row to keep in sync. */
export const PAGE_COLUMNS = 'lg:grid-cols-[1fr_343px]';

/** How far down the side column starts, from the top of the row it shares with the banner and the
 *  glass container: 500px, level with the list's toolbar. Neither block has a fixed height to derive
 *  it from. Only at `lg`, where the page has two columns (see src/responsive-utilities.css). */
export const SIDE_COLUMN_TOP_OFFSET = 'lg:pt-[500px]';

/** The list page's content-row cap: 1400px, posed on the row itself rather than on the ambient
 *  page shell, whose cap depends on the padding each context adds around the row. */
export const CONTENT_ROW_MAX_WIDTH = 'max-w-[1400px] mx-auto';

/** The plan page's content cap: 1336px, a narrower column than the list's. */
export const PLAN_CONTENT_MAX_WIDTH = 'max-w-[1336px] mx-auto';

/** Air above the plan's transcript section, on top of the 24px `CARD_STACK_GAP`: 150 + 24 puts it
 *  174px below the section before it, as the design sets the part that comes after the meeting apart
 *  from the plan written before it. That air frames the arrow drawn over the section, which only shows
 *  from `lg`; below it the section follows the page's ordinary gap. */
export const TRANSCRIPT_SECTION_OFFSET = 'lg:mt-[150px]';

/** Air above the plan's career-aspirations block, on top of the 24px `CARD_STACK_GAP`: one more
 *  unit of that same rhythm (`mt-6`, six 4px steps) puts the gradient band 48px below the agenda it
 *  follows, so the band opens a part of the plan instead of reading as the agenda's own footer. */
export const CAREER_SECTION_OFFSET = 'mt-6';

/** Air above the plan's meeting-notes card, on top of the 24px `CARD_STACK_GAP`: 100 + 24 puts the
 *  card 124px below the transcript it follows, which is the distance the design sets between what the
 *  meeting produced and what is written after it. Below `lg` the card follows the page's ordinary gap. */
export const NOTES_SECTION_OFFSET = 'lg:mt-[100px]';

/**
 * The outline a white field of the meetings wears: the library's own field border, the one it draws
 * on `TextInput` and `MultilineInput` by itself and leaves to a caller on a date field. Named here
 * rather than written at the field, so the shade is one line to change and cannot drift from the one
 * the growth forms wear.
 */
export const FIELD_BORDER = 'border-border-field';

/** Banner description on the list page: 14px, readable on frosted glass. */
export const BANNER_DESCRIPTION = 'text-[14px]';

/** Intro card description: 11px. */
export const INTRO_DESCRIPTION = 'text-[11px]';

/** List glass container padding: 16px, then 32px from `lg` -- the `p-4` and `p-8` steps, named here
 *  because `Card` offers no such pair through its own `padding` prop. Two exclusive variants rather
 *  than a base class and its `lg:` variant (see src/responsive-utilities.css). */
export const GLASS_CONTAINER_PADDING = 'max-lg:p-[16px] lg:p-[32px]';

/** Search field width: 320px, never wider than its row. A width rather than a cap: in a wrapping
 *  flex row an item without one shrinks to its content and clips the placeholder. */
export const SEARCH_FIELD_WIDTH = 'w-[320px] max-w-full';

/** The meeting card's identity column is 210px wide, and that is also the room the theme-and-date
 *  column needs before it will share a line with it. One basis for the two: a card too narrow for
 *  both wraps the theme under the identity rather than squeezing its title to nothing. */
export const MEETING_CARD_COLUMN = 'basis-[210px]';
